import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import type { DashboardWidget } from "../types/dashboard/DashboardConfig";
import type { SearchFilters } from "../v2/components/FilterBar/filterChipDefs";
import { filtersToParams } from "../v2/pages/search/filterParams";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

/* ── Types ────────────────────────────────────────────────────────── */

export type DrillDescriptor =
  | { kind: "none" }
  | { kind: "legalHold" }
  | { kind: "purgingNext30d" }
  | { kind: "tag"; value: string }
  | { kind: "field"; field: keyof SearchFilters; value: string }
  | { kind: "dateRange"; from: string; to: string };

export type DashboardVersion = "v1" | "v2";

/**
 * The widget's existing filters (what was set in the Widget Builder, if any).
 * Built-in widgets pass an empty object.
 */
export type WidgetFilters = Partial<SearchFilters>;

/* ── Constants ────────────────────────────────────────────────────── */

const SEARCH_PATH: Record<DashboardVersion, string> = {
  v1: "search",
  v2: "v2/search",
};

const DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss";

const DRILLABLE_SEARCH_FIELDS: ReadonlySet<keyof SearchFilters> = new Set<
  keyof SearchFilters
>(["agentName", "direction", "interactionType", "skill", "group", "tags"]);

const INTERVAL_PARSE: Record<
  string,
  { format: string; addUnit: dayjs.ManipulateType; addAmount: number }
> = {
  "1y": { format: "YYYY", addUnit: "year", addAmount: 1 },
  "1M": { format: "YYYY-MM", addUnit: "month", addAmount: 1 },
  "1w": { format: "YYYY-MM-DD", addUnit: "day", addAmount: 7 },
  "1d": { format: "YYYY-MM-DD", addUnit: "day", addAmount: 1 },
};

/* ── canDrillCustomWidget ─────────────────────────────────────────── */

export function canDrillCustomWidget(widget: DashboardWidget): boolean {
  const cfg = widget.config;
  if (!cfg) return false;

  const aggType = (cfg.aggregationType ?? "terms").toLowerCase();

  // Average aggregations don't drill — there's no meaningful filtered set
  // associated with an average across the population.
  if (aggType === "average") return false;
  if (aggType === "count") return true;

  if (aggType === "datehistogram") {
    const interval = cfg.interval ?? "1M";
    return Object.prototype.hasOwnProperty.call(INTERVAL_PARSE, interval);
  }

  if (aggType === "terms") {
    const field = cfg.field;
    if (!field) return false;
    return DRILLABLE_SEARCH_FIELDS.has(field as keyof SearchFilters);
  }

  return false;
}

/* ── resolveDrillDescriptor ─────────────────────────────────────────── */

export function resolveDrillDescriptor(
  widget: DashboardWidget,
  clickedIndex: number,
  labels: string[],
): DrillDescriptor | null {
  const config = widget.config;
  if (!config) return null;

  const aggType = (config.aggregationType ?? "terms").toLowerCase();

  if (aggType === "count") {
    return { kind: "none" };
  }
  if (aggType === "average") {
    return null;
  }

  const label = labels[clickedIndex];
  if (label === undefined) return null;

  if (aggType === "terms") {
    const field = config.field;
    if (!field) return null;
    return {
      kind: "field",
      field: field as keyof SearchFilters,
      value: label,
    };
  }

  if (aggType === "datehistogram") {
    const interval = (config.interval ?? "1M") as string;
    const spec = INTERVAL_PARSE[interval];
    if (!spec) return null;

    const start = dayjs.utc(label, spec.format, true);
    if (!start.isValid()) return null;

    const end = start.add(spec.addAmount, spec.addUnit);
    return {
      kind: "dateRange",
      from: start.format(DATE_FORMAT),
      to: end.format(DATE_FORMAT),
    };
  }

  return null;
}

/* ── buildDrillUrl ────────────────────────────────────────────────── */

export function buildDrillUrl(
  customerId: string,
  version: DashboardVersion,
  widgetFilters: WidgetFilters,
  descriptor: DrillDescriptor,
): string {
  const merged = applyDescriptor(widgetFilters, descriptor);
  const qs = filtersToParams(merged).toString();
  const path = `/customers/${customerId}/${SEARCH_PATH[version]}`;
  return qs ? `${path}?${qs}` : path;
}

function applyDescriptor(
  base: WidgetFilters,
  d: DrillDescriptor,
): SearchFilters {
  switch (d.kind) {
    case "none":
      return { ...base };
    case "legalHold":
      return { ...base, legalHold: true };
    case "purgingNext30d": {
      const now = dayjs();
      return {
        ...base,
        fromRetention: now.format(DATE_FORMAT),
        toRetention: now.add(30, "day").format(DATE_FORMAT),
        hasRetention: true,
      };
    }
    case "tag":
      return { ...base, tags: [d.value] };
    case "field": {
      const value = d.field === "tags" ? [d.value] : d.value;
      return { ...base, [d.field]: value } as SearchFilters;
    }
    case "dateRange":
      return { ...base, from: d.from, to: d.to };
  }
}
